import './assets/main.css'

import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import Notifications from 'notiwind'
import { useAuthGlobalStore } from '@/stores/authGlobalStore'
import App from './App.vue'
import router from './router'
import * as Sentry from '@sentry/vue'

const app = createApp(App)

const SENTRY_IGNORED_CONSOLE_MESSAGES = [
  'Please login to access this resource',
  'The requested resource does not exist or has been deleted',
  'connection problems..',
  'connection canceled..'
]

Sentry.init({
  app,
  dsn: 'https://989b64d14a94c7cc0c4cd665f8711514@o4507588414799872.ingest.de.sentry.io/4511464206827600',
  environment: import.meta.env.MODE || 'development',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false
    }),
    Sentry.captureConsoleIntegration({
      levels: ['error', 'warn']
    })
  ],
  beforeSend(event) {
    const message =
      event.message ||
      (typeof event.logentry?.message === 'string' ? event.logentry.message : '')
    if (SENTRY_IGNORED_CONSOLE_MESSAGES.some((snippet) => message.includes(snippet))) {
      return null
    }
    return event
  },
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0
})
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(Notifications)
app.use(pinia)

const authGlobalStore = useAuthGlobalStore()

router.beforeEach(async (to, from) => {
  if (to.meta.requiresAdminAuth) {
    if (!authGlobalStore.isStaffLoggedIn) {
      return {
        name: 'adminLogin',
        query: { redirect: to.fullPath }
      }
    }

    try {
      const user_data = await authGlobalStore.getAdminAuthUser()
      authGlobalStore.setAdminAuthUser(user_data)
    } catch (error) {
      authGlobalStore.adminLogout().then(() => {
        router.push({ name: 'adminLogin', query: { redirect: to.fullPath } })
      })
    }
  } else if (to.meta.requiresClientAuth) {
    if (!authGlobalStore.isClientLoggedIn) {
      return {
        name: 'clientLogin',
        query: { redirect: to.fullPath }
      }
    }
    try {
      const user_data = await authGlobalStore.getClientAuthUser()
      authGlobalStore.setClientAuthUser(user_data)
    } catch (error) {
      authGlobalStore.clientLogout().then(() => {
        router.push({ name: 'clientLogin', query: { redirect: to.fullPath } })
      })
    }
  }

  const t = to.meta?.title
  document.title =
    typeof t === 'function' ? t(to) : typeof t === 'string' ? t : ''
})

app.use(router)
// app.mount('#app')
// wait until router is ready before mounting to ensure hydration match
// otherwise test will fail to use store
router.isReady().then(async () => {
  if (Capacitor.isNativePlatform()) {
    await StatusBar.setStyle({ style: Style.Dark })
  }
  app.mount('#app')
})
